"use client";

import { useMemo, useState } from "react";
import Link from "@/components/locale-link";
import {
  Checkbox,
  CopyButton,
  Notice,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  RESULT_CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { genericQuery } from "@/lib/tool-kit/research-workflow/boolean";
import {
  builderQuery,
  EMPTY_PICO_INPUT,
  elementTerms,
  frameworkName,
  PICO_ELEMENTS,
  PICO_TYPES,
  type PicoElement,
  type PicoInput,
  type PicoType,
  picoQuestion,
  picoSearchString,
  searchElements,
} from "@/lib/tool-kit/research-workflow/pico";

/** English strings. A translated content file overrides these through `ui`. */
export const PICO_QUESTION_GENERATOR_STRINGS = {
  typeLabel: "Question type",
  typeOptions: {
    therapy: "Therapy",
    prevention: "Prevention",
    diagnosis: "Diagnosis",
    prognosis: "Prognosis",
    aetiology: "Aetiology or harm",
    meaning: "Meaning",
  },
  typeHints: {
    therapy: "Does an intervention work better than the alternative?",
    prevention: "Does an intervention lower the risk of a condition?",
    diagnosis: "How accurately does a test identify a condition?",
    prognosis: "What is likely to happen to people with a given factor?",
    aetiology: "Does an exposure cause harm or disease?",
    meaning: "How do people experience a situation or condition?",
  },
  designLead: "Usually answered by {design}.",
  designHints: {
    therapy: "randomised controlled trials, and systematic reviews of them",
    prevention: "randomised controlled trials, and systematic reviews of them",
    diagnosis:
      "cross-sectional studies that compare the test with a reference standard",
    prognosis: "cohort studies that follow people from a common starting point",
    aetiology: "cohort and case-control studies",
    meaning: "qualitative studies, and syntheses of them",
  },
  meaningNote:
    "Meaning questions are qualitative. Many reviews frame them with PICo — Population, phenomenon of Interest, Context — or SPIDER instead of a comparison and an outcome.",
  elementsHeading: "Your PICO elements",
  pLabel: "P — Population or problem",
  iLabels: {
    therapy: "I — Intervention",
    prevention: "I — Preventive intervention",
    diagnosis: "I — Index test",
    prognosis: "I — Prognostic factor",
    aetiology: "I — Exposure or risk factor",
    meaning: "I — Phenomenon of interest",
  },
  cLabels: {
    therapy: "C — Comparison (optional)",
    prevention: "C — Comparison (optional)",
    diagnosis: "C — Reference standard or another test (optional)",
    prognosis: "C — Absence of the factor (optional)",
    aetiology: "C — No exposure or another exposure (optional)",
    meaning: "C — Not used in meaning questions",
  },
  oLabels: {
    therapy: "O — Outcome",
    prevention: "O — Condition or event prevented",
    diagnosis: "O — Condition diagnosed",
    prognosis: "O — Outcome predicted",
    aetiology: "O — Harm or condition",
    meaning: "O — Not used in meaning questions",
  },
  tLabel: "T — Time frame (optional, makes PICOT)",
  sLabel: "S — Study design (optional, makes PICOS)",
  placeholders: {
    p: "e.g. older adults living at home",
    i: "e.g. a home-based exercise programme",
    c: "e.g. usual care",
    o: "e.g. falls",
    t: "e.g. 12 months",
    s: "e.g. randomised controlled trials",
  },
  synonymsLabel:
    "Search terms for {element} — keywords and synonyms, one per line",
  synonymsHint: "Leave blank to search for the element as written above.",
  synonymPlaceholders: {
    p: "e.g. older adults\nelderly\naged",
    i: "e.g. exercise*\nphysical activity\nstrength training",
    c: "e.g. usual care\nstandard care",
    o: "e.g. fall*\naccidental falls",
    t: "e.g. 12 months",
    s: "e.g. randomized controlled trial\nRCT",
  },
  includeLabel: "Use in the search string",
  questionHeading: "Your {framework} question",
  allTypesHeading: "The same elements, as every question type",
  searchHeading: "Search terms",
  elementColumn: "Element",
  blockColumn: "Search block",
  searchStringLabel: "Combined search string",
  builderLink:
    "Turn these terms into PubMed, Scopus, Web of Science and other database strings in the Boolean Search String Builder",
  emptySearch:
    "Fill in an element marked “Use in the search string” to see its search block.",
  copy: "Copy",
  copied: "Copied!",
  elementNames: { p: "P", i: "I", c: "C", o: "O", t: "T", s: "S" },
  withComparison: {
    therapy: "In {p}, how does {i} compared with {c} affect {o}{time}?",
    prevention:
      "In {p}, does {i} compared with {c} reduce the risk of {o}{time}?",
    diagnosis:
      "In {p}, how accurate is {i} compared with {c} for diagnosing {o}?",
    prognosis:
      "In {p}, how does {i} compared with {c} change the likelihood of {o}{time}?",
    aetiology:
      "Are {p} exposed to {i} at greater risk of {o} than those exposed to {c}{time}?",
    meaning: "How do {p} experience {i}{time}?",
  },
  withoutComparison: {
    therapy: "In {p}, how does {i} affect {o}{time}?",
    prevention: "In {p}, does {i} reduce the risk of {o}{time}?",
    diagnosis: "In {p}, how accurate is {i} for diagnosing {o}?",
    prognosis: "In {p}, how does {i} change the likelihood of {o}{time}?",
    aetiology:
      "Are {p} exposed to {i} at greater risk of {o} than those who are not{time}?",
    meaning: "How do {p} experience {i}{time}?",
  },
  timeClause: {
    therapy: " within {t}",
    prevention: " over {t}",
    diagnosis: "",
    prognosis: " over {t}",
    aetiology: " over {t}",
    meaning: " during {t}",
  },
  missing: {
    p: "[population]",
    i: "[intervention]",
    c: "[comparison]",
    o: "[outcome]",
    t: "[time]",
    s: "[study design]",
  },
} as const;

type Strings = typeof PICO_QUESTION_GENERATOR_STRINGS;

function elementLabel(element: PicoElement, type: PicoType, s: Strings) {
  switch (element) {
    case "p":
      return s.pLabel;
    case "i":
      return s.iLabels[type];
    case "c":
      return s.cLabels[type];
    case "o":
      return s.oLabels[type];
    case "t":
      return s.tLabel;
    default:
      return s.sLabel;
  }
}

function SearchTable({
  input,
  strings,
}: {
  input: PicoInput;
  strings: Strings;
}) {
  const elements = searchElements(input);
  if (elements.length === 0) {
    return <Notice>{strings.emptySearch}</Notice>;
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            <th className={TH} scope="col">
              {strings.elementColumn}
            </th>
            <th className={TH} scope="col">
              {strings.blockColumn}
            </th>
          </tr>
        </thead>
        <tbody>
          {elements.map((element) => (
            <tr key={element}>
              <td className={`${TD} font-semibold`}>
                {strings.elementNames[element]}
              </td>
              <td className={`${TD} break-words font-mono`}>
                {genericQuery([elementTerms(input, element)])}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PicoQuestionGeneratorWidget() {
  const strings = useToolUi(PICO_QUESTION_GENERATOR_STRINGS);
  const [type, setType] = useState<PicoType>("therapy");
  const [input, setInput] = useState<PicoInput>(EMPTY_PICO_INPUT);

  const setValue = (element: PicoElement, value: string) =>
    setInput((current) => ({
      ...current,
      values: { ...current.values, [element]: value },
    }));
  const setSynonyms = (element: PicoElement, value: string) =>
    setInput((current) => ({
      ...current,
      synonyms: { ...current.synonyms, [element]: value },
    }));
  const setInclude = (element: PicoElement, value: boolean) =>
    setInput((current) => ({
      ...current,
      include: { ...current.include, [element]: value },
    }));

  const question = picoQuestion(input, type, strings);
  const framework = frameworkName(input);
  const searchString = picoSearchString(input);
  const link = useMemo(() => {
    const query = builderQuery(input);
    return query
      ? `/tools/boolean-search-builder?${query}`
      : "/tools/boolean-search-builder";
  }, [input]);

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.typeLabel}
        name="pico-type"
        onChange={setType}
        options={PICO_TYPES.map((value) => ({
          value,
          label: strings.typeOptions[value],
        }))}
        value={type}
      />
      <p className="text-muted-foreground text-sm">
        {strings.typeHints[type]}{" "}
        {t(strings.designLead, { design: strings.designHints[type] })}
      </p>
      {type === "meaning" ? <Notice>{strings.meaningNote}</Notice> : null}

      <section className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.elementsHeading}</h2>
        {PICO_ELEMENTS.map((element) => (
          <div className={`${CARD} space-y-3`} key={element}>
            <TextField
              id={`pico-${element}`}
              label={elementLabel(element, type, strings)}
              onChange={(value) => setValue(element, value)}
              placeholder={strings.placeholders[element]}
              value={input.values[element]}
            />
            <TextAreaField
              hint={strings.synonymsHint}
              id={`pico-${element}-terms`}
              label={t(strings.synonymsLabel, {
                element: strings.elementNames[element],
              })}
              onChange={(value) => setSynonyms(element, value)}
              placeholder={strings.synonymPlaceholders[element]}
              rows={2}
              value={input.synonyms[element]}
            />
            <Checkbox
              checked={input.include[element]}
              label={strings.includeLabel}
              onChange={(value) => setInclude(element, value)}
            />
          </div>
        ))}
      </section>

      <section aria-live="polite" className={`${RESULT_CARD} space-y-3`}>
        <h2 className={WIDGET_H2}>
          {t(strings.questionHeading, { framework })}
        </h2>
        <p className="font-medium text-foreground text-lg leading-relaxed">
          {question}
        </p>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          small
          text={question}
        />
      </section>

      <section className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.allTypesHeading}</h3>
        <ul className="space-y-2">
          {PICO_TYPES.map((value) => {
            const text = picoQuestion(input, value, strings);
            return (
              <li
                className="flex items-start justify-between gap-4 rounded-lg border border-border bg-card p-4"
                key={value}
              >
                <p className="text-foreground text-sm leading-relaxed">
                  <span className="font-medium">
                    {strings.typeOptions[value]}
                  </span>
                  {" — "}
                  {text}
                </p>
                <CopyButton
                  copiedLabel={strings.copied}
                  label={strings.copy}
                  small
                  text={text}
                />
              </li>
            );
          })}
        </ul>
      </section>

      <section className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.searchHeading}</h3>
        <SearchTable input={input} strings={strings} />
        {searchString ? (
          <div className={`${CARD} space-y-3`}>
            <p className="font-medium text-foreground text-sm">
              {strings.searchStringLabel}
            </p>
            <p className="break-words font-mono text-foreground text-sm">
              {searchString}
            </p>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copy}
              small
              text={searchString}
            />
          </div>
        ) : null}
        <p className="text-sm">
          <Link
            className="font-medium text-primary underline underline-offset-2"
            href={link}
          >
            {strings.builderLink}
          </Link>
        </p>
      </section>
    </div>
  );
}
